import { AmbientLight, Box3, ClampToEdgeWrapping, DataTexture, DirectionalLight, DoubleSide, FrontSide, Group, InstancedMesh, LinearFilter, Material, Matrix4, Mesh, MeshBasicMaterial, MeshPhongMaterial, MeshStandardMaterial, NearestFilter, Object3D, OrthographicCamera, RGBAFormat, SphereGeometry, SRGBColorSpace,Vector3, WebGLRenderTarget } from "three";
import Util, { TreeObjects, meshTypeEnum, panelDims, threeColors } from "./Utility";
import * as d3 from 'd3';
import { PanelGeometry } from "./panelGeometry";
import { DesignState } from "@design/store/design.reducer";
import { Store } from "@ngrx/store";
import { fromRoofSectionSelectors } from "@design/store/roof-section";
import { switchMap, map, take, takeUntil } from "rxjs/operators";
import { Observable, Subject, Subscription, of } from "rxjs";
import { fromVertexSelectors } from "@design/store/vertex";
import { fromMetaDataSelectors } from "@design/store/meta-data";
import { fromShadingActions, fromShadingSelectors } from "@design/store/shading";
import { fromPanelArraySelectors } from "@design/store/panel-array/panel-array.selector";
import { AuthService } from "src/app/helpers/auth.service";
import { ShadingStatus } from "@design/models/shading.model";
import { NotificationType } from "@lib-ui/lib/atomic/enact-alert-snack-bar/notification.message";
import { openNotificationService } from "@lib-ui/lib/atomic/enact-alert-snack-bar/openNotification.service";
import { TranslateService } from "@ngx-translate/core";
import { Injectable, Injector } from "@angular/core";
import { EngineService } from "../threejs.service";
import { fromPanelArrayActions } from "@design/store/panel-array";
import { fromPanelSelectors } from "@design/store/panel/panel.selector";
import { InstancedMeshUtil, PanelTextureEnum } from "../utils/instanceMesh.util";
import { fromUndoRedoActions } from "@design/store/undo-redo/undo-redo.action";
import { interpolateInferno, scaleSequential } from "d3";
import SunCalc from 'suncalc';
import { ImageCapturer } from "../services/image-capturing.service";

declare let getJD: any;
declare let calcTimeJulianCent: any;
declare let calcAzEl: any;
declare let calcSunriseSetUTC: any;
const ts = require("@mapbox/timespace");

const configs = {
    CANVAS_HEIGHT: 2000,
    CANVAS_WIDTH: 2000,
    CAMERA_NEAR: 0.1,
    LAT: 37.71688314718149,
    LON: -121.87104505953252,
    UTCOFFSET: -7
}

const colorScale = scaleSequential(interpolateInferno)
    .domain([0, 100]);
const panelcolorScale = d3
    .scaleSequential(d3.interpolateWarm)
    .domain([0, 101]);

@Injectable({ providedIn: 'root' })
export class Shader {
    private latitude: number = configs.LAT;
    private longitude: number = configs.LON;
    private utcOffset: number = configs.UTCOFFSET;
    private orthoCamera!: OrthographicCamera;
    private target!: WebGLRenderTarget;
    private directionalLight: DirectionalLight = new DirectionalLight(0xffffff, 10);
    public radianceMaterialTexture: any = undefined;
    public height = configs.CANVAS_HEIGHT;
    public width = configs.CANVAS_WIDTH;
    private allVertices: any[] = [];
    private sunPositionArray!: Vector3[][];
    private sunRiseArray!: number[];
    private bbox!: Box3;

    mapPlane: Mesh | undefined;
    isRadianceVisible = false;
    panelShadeData!: Map<string, { hourly: any[]; monthly: any[]; yearly: any[] }>;
    panelCords: Map<string, Vector3[]> | undefined;
    hoverPanel: { id: string | undefined, texture: PanelTextureEnum | undefined, instanceId: number | undefined, panelArrayId: string | undefined } = {
        id: undefined,
        texture: undefined,
        instanceId: undefined,
        panelArrayId: undefined
    };

    t!: number;
    roofPanelProperties: Map<string, {
        panelCoords: Map<string, Vector3[]> | undefined;
        maxObj: {
            monthly: number[];
            yearly: number[];
        } | undefined;
        radianceUv: number[] | undefined;
    }> = new Map();

    enactShaderWorker!: Worker;

    constructor(private engServ: EngineService,
        private store: Store<DesignState>, private panelGeometry: PanelGeometry, private authService: AuthService,
        private notificationService: openNotificationService, private translate: TranslateService,
        private injector: Injector) {
        this.store = store;
    }

    init() {
        this.setupRenderTarget();
        this.setupOrtho();
        this.setupDirectionalLight();
        this.getLatLongFromStore();
    }


    /**
     * Calculates the sun's position in 3D space for a given date and location.
     * Uses SunCalc to determine the sun's azimuth and elevation, then converts
     * these angles to a Three.js Vector3 position.
     * @param date The date and time for which to calculate the sun's position.
     * @returns Vector3 representing the sun's position in the scene.
     */
    private getSunLightPosition(date: Date) {
        // Get sun position (azimuth and altitude) from SunCalc
        const sunPos = SunCalc.getPosition(date, this.latitude, this.longitude);

        // Adjust azimuth: SunCalc azimuth is from south, positive westward.
        // Rotate so 0 = east, π/2 = north (Three.js convention)
        const azimuth = sunPos.azimuth + Math.PI / 2;
        const elevation = sunPos.altitude;

        // Convert spherical coordinates to Cartesian (scene units)
        const radius = 2000;
        const x = radius * Math.cos(elevation) * Math.cos(azimuth);
        const y = -radius * Math.cos(elevation) * Math.sin(azimuth);
        const z = radius * Math.sin(elevation);

        return new Vector3(x, y, z);
    }

    private getLatLongFromStore() {
        this.latitude = Number(this.authService.getItem('c_lat'));
        this.longitude = Number(this.authService.getItem('c_long'));
        const point = [this.longitude, this.latitude];
        const timestamp = Date.now();
        const time = ts.getFuzzyLocalTimeFromPoint(timestamp, point);
        this.utcOffset = (time._offset) / 60;
    }

    private cleanupResources() {
        if (this.target) {
            this.target.dispose();
        }
        if (this.orthoCamera) {
            this.engServ.scene.remove(this.orthoCamera);
        }
        if (this.directionalLight) {
            // this.engServ.scene.remove(this.directionalLight);
        }
    }

    private setupRenderTarget() {
        this.cleanupResources();
        if (this.target) this.target.dispose();

        // const format = parseFloat(String(DepthFormat));
        // const type = parseFloat(String(UnsignedShortType));
        const pixelSize = 2;
        const rows = Math.floor(this.height);
        const columns = Math.floor(this.width);

        this.target = new WebGLRenderTarget(columns, rows);
        this.target.texture.minFilter = NearestFilter;
        this.target.texture.magFilter = NearestFilter;
        this.target.stencilBuffer = false;
        //     this.target.depthTexture = new DepthTexture(this.target.width, this.target.height);
        //     this.target.depthTexture.format = format;
        //     this.target.depthTexture.type = type;
    }
    private setupOrtho() {
        this.cleanupResources();
        if (!this.orthoCamera) {
            this.orthoCamera = new OrthographicCamera(-this.width / 2, this.width / 2, this.height / 2, -this.height / 2, 0, 2000);
            this.engServ.scene.add(this.orthoCamera);
            // const helper = new CameraHelper(this.orthoCamera);
            // this.orthoCamera.add(helper);
            this.orthoCamera.position.set(0, 0, 10);
            this.adjustOrthoCamera();
        }
    }

       private setupDirectionalLight() {
        // this.cleanupResources();
        this.directionalLight.castShadow = true;
        this.directionalLight.shadow.camera.near = 0; // default
        this.directionalLight.shadow.camera.far = 7000;
        this.directionalLight.shadow.camera.left = -4000; // Adjust based on scene size
        this.directionalLight.shadow.camera.right = 4000;
        this.directionalLight.shadow.camera.top = 4000;
        this.directionalLight.shadow.camera.bottom = -4000;
        this.directionalLight.shadow.mapSize.width = 4096; // Adjust based on scene size
        this.directionalLight.shadow.mapSize.height = 4096;
        this.directionalLight.shadow.bias = -0.000009;
    }


    /**
     * Updates the directional light's shadow camera bounds to fit the ground plane (MapPlane).
     * This ensures shadows are rendered only within the visible area of the ground.
     * Also updates the shadow map size to half of the renderer's max texture size.
     * Disposes the old shadow map to force regeneration.
     */
    private updateDirectionalLightShadowBounds() {
        // Find the ground plane object in the scene
        const groundPlane = this.engServ.scene.getObjectByName(meshTypeEnum.MapPlane);

        if (groundPlane) {
            // Compute the bounding box of the ground plane
            const bbox = new Box3().setFromObject(groundPlane);
            const min = bbox.min;
            const max = bbox.max;

            // Set the shadow camera bounds to match the ground plane, but double the size
            const centerX = (min.x + max.x) / 2;
            const centerY = (min.y + max.y) / 2;
            const width = (max.x - min.x);
            const height = (max.y - min.y);

            this.directionalLight.shadow.camera.left = centerX - width;
            this.directionalLight.shadow.camera.right = centerX + width;
            this.directionalLight.shadow.camera.bottom = centerY - height;
            this.directionalLight.shadow.camera.top = centerY + height;

            // Ensure near and far planes are set to fully encompass the scene, especially for low z values
            this.directionalLight.shadow.camera.near = 0.1;
            this.directionalLight.shadow.camera.far = Math.max(7000, Math.abs(this.directionalLight.position.z) + 2000);

            this.directionalLight.shadow.camera.updateProjectionMatrix();
        }

        // Set shadow map size to half of the renderer's maximum texture size
        this.directionalLight.shadow.mapSize.width = this.engServ.renderer.capabilities.maxTextureSize / 2;
        this.directionalLight.shadow.mapSize.height = this.engServ.renderer.capabilities.maxTextureSize / 2;
        // this.directionalLight.shadow.bias = -0.0002;

        // Dispose of the old shadow map to force regeneration
        if (this.directionalLight.shadow.map !== null) {
            this.directionalLight.shadow.map.dispose();
            this.directionalLight.shadow.map = null;
        }
    }

    private adjustDirectionalLight(position: Vector3, roofList: any[] = []) {
        // Move the light
        this.directionalLight.position.set(
            position.x,
            position.y,
            position.z);
        this.setPanelsWhite(roofList);
        this.engServ.renderer.shadowMap.needsUpdate = true;
    }

    private adjustOrthoCamera(withPanels: boolean = false): void {
        if (!withPanels) {
            this.height = configs.CANVAS_HEIGHT / 2;
            this.width = configs.CANVAS_WIDTH / 2;
        }
        const group = new Group();
        const group2 = new Group();
        let haveObjects = false;

        // Categorize scene children into groups based on their names
        this.engServ.scene.children.forEach(child => {
            if (child.name.includes('roof') || child.name.includes('Tree')) {
                haveObjects = true;
                const clone = child.clone();
                clone.getObjectByName(meshTypeEnum.pillerArray)?.traverse((p: Object3D) => {p.removeFromParent()});
                group.add(clone);
            }
            if (!withPanels && child.name.includes(meshTypeEnum.MapPlane)) {
                group2.add(child.clone());
            }
        });

        // Determine the bounding box based on the presence of objects or condition
        const targetGroup = withPanels && haveObjects ? group : group2;
        const boundingBox = new Box3().setFromObject(targetGroup);

        // Calculate center and size of the bounding box
        const center = new Vector3();
        const size = new Vector3();
        boundingBox.getCenter(center);
        boundingBox.getSize(size);
        this.bbox = boundingBox;

        // Determine the maximum size and the diagonal length of the bounding box
        const maxSize = Math.max(size.x, size.y, size.z);
        const diagonalLength = size.length();

        if (withPanels) {
            this.width = 4 * diagonalLength / (Util.getFeetToMeter(panelDims.WIDTH) / Util.siteConfig?.meterPerPixel);
            this.height = 4 * diagonalLength / (Util.getFeetToMeter(panelDims.WIDTH) / Util.siteConfig?.meterPerPixel);
            if (this.width < 1000 || this.height < 1000) {
                this.width = 1000;
                this.height = 1000;
            }
        }
        // Replace the current camera with the new camera
        this.orthoCamera = new OrthographicCamera(
            -this.width / 2,
            this.width / 2,
            this.height / 2,
            -this.height / 2,
            0.1, // Near clipping plane
            5000 // Far clipping plane
        );

        // Adjust camera position, orientation, and zoom
        this.orthoCamera.position.copy(center).add(new Vector3(0, 0, maxSize));
        this.orthoCamera.lookAt(center);
        this.orthoCamera.rotateZ(-Math.PI / 2);
        this.orthoCamera.zoom = withPanels ? this.width / diagonalLength : this.width / 2000;

        this.orthoCamera.updateProjectionMatrix();
        this.orthoCamera.updateMatrixWorld();
    }


    simulate() {
        this.stopManualSim();
        // this.disablePointCloud();
        this.updateDirectionalLightShadowBounds();
        this.enactShaderWorker = new Worker(new URL('../enact-shader.worker.ts', import.meta.url), { type: 'module' });
        this.addWorkerListener();

        this.t = new Date().getTime();
        this.hoverPanel = { id: undefined, texture: undefined, instanceId: undefined, panelArrayId: undefined }
        this.store
            .select(fromRoofSectionSelectors.allRoofSections)
            .pipe(take(1))
            .subscribe((v) => {
                const roofList: Object3D[] = [];
                for (let i = 0; i < v.length; i++) {
                    const objs = this.engServ.scene.getObjectsByProperty('name', v[i].id);
                    let roof;
                    objs.forEach(obj => {
                        if (obj.type == 'Object3D') {
                            roof = obj;
                        }
                    })
                    if (roof) { roofList.push(roof) }
                }

                const d = new Date();
                const sp = this.sunPositionArrayFromYear(d.getFullYear());
                this.sunPositionArray = sp.sunPositionArray;
                this.sunRiseArray = sp.sunriseArray;

                this.prepareForSimulate(roofList);

                this.adjustOrthoCamera(false);
                this.setupRenderTarget();
                this.engServ.renderer.setRenderTarget(this.target);
                const buffersWithoutPanels = this.simulation(roofList);

                this.adjustOrthoCamera(true);
                for (let i = 0; i < roofList.length; i++) {
                    if (roofList[i]) {
                        this.removeHiddenPanels(roofList[i]);
                        this.splitEastWestPanels(roofList[i]);
                    }
                }


                this.togglePanelsShadow(roofList, true);
                const buffersWithPanels = this.simulation(roofList, false);

                this.panelShadeData = new Map();
                this.roofPanelProperties = new Map();
                for (let i = 0; i < roofList.length; i++) {
                    if (roofList[i]) {
                        this.restoreHiddentPanels(roofList[i]);
                        this.restoreEastWestPanels(roofList[i]);
                        (this.roofPanelProperties as any)[roofList[i].name] = this.getRoofPanelProperties(roofList[i]);
                    }
                }
                this.enactShaderWorker.postMessage({
                    message: 'shading',
                    data: {
                        buffersWithoutPanels, buffersWithPanels, columns: this.target.width, columnsWithoutPanels: configs.CANVAS_WIDTH / 2, rows: this.target.height, rowsWithoutPanels: configs.CANVAS_HEIGHT / 2, roofPanelProperties: JSON.parse(JSON.stringify(this.roofPanelProperties)), sunRiseArray: this.sunRiseArray
                    },

                });
                this.engServ.renderer.setRenderTarget(null);
                this.recoverSceneAfterSimulate(roofList);
                if (Util.debugThreejs) {
                    setTimeout(() => {
                        this.wallShadow(roofList, true);
                        this.startManulSimulation();
                    }, 3000);
                }
            });

    }

    private wallShadow(roofList: any[], show: boolean) {
        const mat: any = new MeshPhongMaterial({
            color: threeColors.wallColor,
            side: FrontSide, // Use FrontSide to ensure proper shadow casting and receiving
            shadowSide: DoubleSide // Ensure shadows are cast on both sides
        });
        const obstMat: any = new MeshPhongMaterial({
            color: threeColors.wallColor,
            side: DoubleSide, // Use FrontSide to ensure proper shadow casting and receiving
            shadowSide: DoubleSide // Ensure shadows are cast on both sides
        });

        for (let i = 0; i < roofList.length; i++) {
            const roof = roofList[i];
            const wallGroup = roof.getObjectByName(meshTypeEnum.wallGroup) as Group;
            for (const wall of wallGroup.children) {
                if (wall instanceof Mesh) {
                    wall.material = mat;
                    wall.receiveShadow = show;
                    wall.castShadow = show;
                }
            }
        }

        this.engServ.scene.traverse((object) => {
            if (object.name === meshTypeEnum.obstacleExtrudeWalls) {
                const wallGroup = object as Group;
                for (const wall of wallGroup.children) {
                    if (wall instanceof Mesh) {
                        wall.material = obstMat;
                        wall.receiveShadow = true;
                        wall.castShadow = true;
                    }
                }
            }
        });
    }
    private prepareForSimulate(roofList: any[]) {
        for (let i = 0; i < roofList.length; i++) {
            if (roofList[i]) {
                this.panelGeometry.savePanelTexture(roofList[i]);
            }
        }
        this.setRoofsWhite(roofList);
        for (const child of this.engServ.scene.children) {
            if (child.name.includes('Tree')) {
                child.children.forEach((c) => {
                    ((c as Mesh).material as MeshStandardMaterial).opacity = 0.01;
                    ((c as Mesh).material as MeshStandardMaterial).colorWrite = false;
                    ((c as Mesh).material as MeshStandardMaterial).depthWrite = false;
                })
            }
        }
        this.toggleObstacleGroups(false);
        this.toggleEdgeGroups(false);
        for (let i = 0; i < roofList.length; i++) {
            if (roofList[i]) {
                this.panelGeometry.togglePanels(roofList[i], false);
            }
        }
        // this.setRoofsWhite(roofList);
        this.adjustOrthoCamera();
        this.engServ.scene.getObjectsByProperty('name', 'Anchor').forEach(a => { Util.removeObjWithChildren(a) });
        this.engServ.scene.add(this.directionalLight);
    }

    removeHiddenPanels(roof: Object3D) {
        let fittedRoof: Mesh | undefined = undefined;
        for (const m of roof.children) {
            if (m.name === meshTypeEnum.roofPlane)
                fittedRoof = m as Mesh;
        }
        if (fittedRoof && fittedRoof.parent?.name) {
            const roofId = fittedRoof.parent?.name;
            const roofMesh = this.engServ.scene.getObjectByName(roofId) as Mesh;
            const instancedMesh = roofMesh.getObjectByName(meshTypeEnum.panelArray) as InstancedMesh;
            if (instancedMesh) {
                // get store panel array
                this.store.select(fromPanelArraySelectors.selectPanelArraysByRoofId(roofId))
                    .pipe(take(1))
                    .subscribe(roofPanelArrays => {
                        if (roofPanelArrays?.[0] && roofPanelArrays[0].arrayType !== '2') {
                            this.store.select(fromPanelSelectors.selectPanelsByIds(roofPanelArrays[0].panelIds))
                                .pipe(take(1))
                                .subscribe(panelArr => {
                                    const visiblePanels: any[] = [];

                                    panelArr.forEach((panel) => {
                                        if (panel?.visible) {
                                            visiblePanels.push(panel);
                                        }
                                    });

                                    // Create new InstancedMeshes for east and west panels
                                    const visibleMesh = new InstancedMesh(instancedMesh.geometry, instancedMesh.material, visiblePanels.length);

                                    // Copy instances to the new meshes
                                    visiblePanels.forEach((panel, index) => {
                                        const matrix = new Matrix4();
                                        instancedMesh.getMatrixAt(panel.instanceId, matrix);
                                        visibleMesh.setMatrixAt(index, matrix);
                                    });

                                    // Add the new meshes to the scene
                                    visibleMesh.name = `${meshTypeEnum.panelArray}-hidden`;
                                    // Apply the same transformation matrix as the original instancedMesh
                                    visibleMesh.position.copy(instancedMesh?.parent?.position ?? new Vector3());

                                    visibleMesh.castShadow = true;
                                    visibleMesh.receiveShadow = true;

                                    roofMesh.add(visibleMesh);

                                    // Hide the original instancedMesh
                                    if (instancedMesh.parent) {
                                        instancedMesh.parent.visible = false;
                                    }
                                    console.log('Hidden panels removed successfully.');

                                })
                        }
                    })
            }
        }
    }

    restoreHiddentPanels(roof: Object3D) {
        let fittedRoof: Mesh | undefined = undefined;
        for (const m of roof.children) {
            if (m.name === meshTypeEnum.roofPlane)
                fittedRoof = m as Mesh;
        }
        if (fittedRoof && fittedRoof.parent?.name) {
            const east = fittedRoof.parent.getObjectByName(`${meshTypeEnum.panelArray}-hidden`);
            if (east) {
                this.engServ.scene.remove(east);
                fittedRoof.parent.remove(east);
            }
            const roofId = fittedRoof.parent?.name;
            const roofMesh = this.engServ.scene.getObjectByName(roofId) as Mesh;
            const instancedMesh = roofMesh.getObjectByName(meshTypeEnum.panelArray) as InstancedMesh;
            if (instancedMesh && instancedMesh.parent) {
                instancedMesh.parent.visible = true;
                console.log('Hidden panels restored successfully.');
            }

        }
    }

    splitEastWestPanels(roof: Object3D) {
        let fittedRoof: Mesh | undefined = undefined;
        for (const m of roof.children) {
            if (m.name === meshTypeEnum.roofPlane)
                fittedRoof = m as Mesh;
        }
        if (fittedRoof && fittedRoof.parent?.name) {
            const roofId = fittedRoof.parent?.name;
            const roofMesh = this.engServ.scene.getObjectByName(roofId) as Mesh;
            const instancedMesh = roofMesh.getObjectByName(meshTypeEnum.panelArray) as InstancedMesh;
            if (instancedMesh) {
                // get store panel array
                this.store.select(fromPanelArraySelectors.selectPanelArraysByRoofId(roofId))
                    .pipe(take(1))
                    .subscribe(roofPanelArrays => {
                        if (roofPanelArrays?.[0] && roofPanelArrays[0].arrayType == '2') {
                            this.store.select(fromPanelSelectors.selectPanelsByIds(roofPanelArrays[0].panelIds))
                                .pipe(take(1))
                                .subscribe(panelArr => {
                                    const eastData: any[] = [];
                                    const westData: any[] = [];

                                    panelArr.forEach((panel) => {
                                        if (panel?.visible) {
                                            if (panel?.azimuth === panelArr[0]?.azimuth) {
                                                eastData.push(panel);
                                            } else {
                                                westData.push(panel);
                                            }
                                        }
                                    });

                                    // Create new InstancedMeshes for east and west panels
                                    const eastMesh = new InstancedMesh(instancedMesh.geometry, instancedMesh.material, eastData.length);
                                    const westMesh = new InstancedMesh(instancedMesh.geometry, instancedMesh.material, westData.length);

                                    // Copy instances to the new meshes
                                    eastData.forEach((panel, index) => {
                                        const matrix = new Matrix4();
                                        instancedMesh.getMatrixAt(panel.instanceId, matrix);
                                        eastMesh.setMatrixAt(index, matrix);
                                    });

                                    westData.forEach((panel, index) => {
                                        const matrix = new Matrix4();
                                        instancedMesh.getMatrixAt(panel.instanceId, matrix);
                                        westMesh.setMatrixAt(index, matrix);
                                    });

                                    // Add the new meshes to the scene
                                    eastMesh.name = `${meshTypeEnum.panelArray}-east`;
                                    westMesh.name = `${meshTypeEnum.panelArray}-west`;

                                    eastMesh.castShadow = true;
                                    eastMesh.receiveShadow = true;

                                    westMesh.castShadow = true;
                                    westMesh.receiveShadow = true;

                                    roofMesh.add(eastMesh);
                                    roofMesh.add(westMesh);

                                    // Hide the original instancedMesh
                                    if (instancedMesh.parent) {
                                        instancedMesh.parent.visible = false;
                                    }
                                    console.log('East and West panels split successfully.');

                                })

                        }

                    })
            }
        }
    }

    restoreEastWestPanels(roof: Object3D) {
        let fittedRoof: Mesh | undefined = undefined;
        for (const m of roof.children) {
            if (m.name === meshTypeEnum.roofPlane)
            fittedRoof = m as Mesh;
        }
        if (fittedRoof && fittedRoof.parent?.name) {
            const east = fittedRoof.parent.getObjectByName(`${meshTypeEnum.panelArray}-east`);
            const west = fittedRoof.parent.getObjectByName(`${meshTypeEnum.panelArray}-west`);
            if (east) {
            this.engServ.scene.remove(east);
            fittedRoof.parent.remove(east);
            }
            if (west) {
            this.engServ.scene.remove(west);
            fittedRoof.parent.remove(west);
            }

            const roofId = fittedRoof.parent?.name;
            const roofMesh = this.engServ.scene.getObjectByName(roofId) as Mesh;
            const instancedMesh = roofMesh.getObjectByName(meshTypeEnum.panelArray) as InstancedMesh;
            if (instancedMesh && instancedMesh.parent) {
            instancedMesh.parent.visible = true;
            }
        }
    }

    private simulatetoTime(time: Date) {
        const timestamp = new Date(time.getFullYear(), time.getMonth(), time.getDate(), time.getHours(), time.getMinutes(), 0);
        const sunPosition = this.getSunLightPosition(timestamp);

        // Get directional light from scene using name
        const directionalLight = this.engServ.scene.getObjectByName('SimulationLight') as DirectionalLight;
        if (directionalLight) {
            directionalLight.position.set(
                sunPosition.x,
                sunPosition.y,
                sunPosition.z);

            directionalLight.updateMatrixWorld();
        }
    }

    private addSimultaionLight() {
        // Check if 'SimulationLight' already exists in the scene
        const existingLight = this.engServ.scene.getObjectByName('SimulationLight');
        (this.engServ.scene.getObjectByName('ambLight') as AmbientLight).intensity = 0;

        if (!existingLight) {
            const directionalLight = new DirectionalLight(0xffffff, 2);
            directionalLight.castShadow = true;
            this.directionalLight.shadow.camera.near = 0.5; // default
            this.directionalLight.shadow.camera.far = 7000;
            this.directionalLight.shadow.camera.left = -4000; // Adjust based on scene size
            this.directionalLight.shadow.camera.right = 4000;
            this.directionalLight.shadow.camera.top = 4000;
            this.directionalLight.shadow.camera.bottom = -4000;
            this.directionalLight.shadow.mapSize.set(4096, 4096);

            this.directionalLight.shadow.bias = -0.000009;
            directionalLight.name = 'SimulationLight';
            this.engServ.scene.add(directionalLight);
        }
    }
    private startManulSimulation() {
        const checkbox: any = document.getElementById("simButton");
        if (checkbox == false)
            checkbox.checked = true
        // Check if the sliders already exist

        if (document.getElementById('hourSlider') || document.getElementById('daySlider')) {
            return;
        }

        this.addSimultaionLight();
        // Create the hour slider element
        const hourSlider = document.createElement('input');
        hourSlider.type = 'range';
        hourSlider.id = 'hourSlider';
        hourSlider.min = '0';
        hourSlider.max = '23';
        hourSlider.value = '12';

        // Style the hour slider to position it at the bottom middle of the page
        hourSlider.style.position = 'fixed';
        hourSlider.style.bottom = '70px';
        hourSlider.style.left = '30%';
        hourSlider.style.transform = 'translateX(-50%)';
        hourSlider.style.zIndex = '1000'; // Ensure it is on top of other elements
        hourSlider.style.width = '300px'; // Make the slider wider

        // Append the hour slider to the body
        document.body.appendChild(hourSlider);

        // Create the day slider element
        const daySlider = document.createElement('input');
        daySlider.type = 'range';
        daySlider.id = 'daySlider';
        daySlider.min = '1';
        daySlider.max = '365';
        daySlider.value = '1';

        // Style the day slider to position it at the bottom middle of the page
        daySlider.style.position = 'fixed';
        daySlider.style.bottom = '20px';
        daySlider.style.left = '30%';
        daySlider.style.transform = 'translateX(-50%)';
        daySlider.style.zIndex = '1000'; // Ensure it is on top of other elements
        daySlider.style.width = '300px'; // Make the slider wider

        // Append the day slider to the body
        document.body.appendChild(daySlider);

        // Create text elements for displaying the selected time and month
        const hourLabel = document.createElement('div');
        hourLabel.id = 'hourLabel';
        hourLabel.style.position = 'fixed';
        hourLabel.style.bottom = '90px';
        hourLabel.style.left = '30%';
        hourLabel.style.transform = 'translateX(-50%)';
        hourLabel.style.zIndex = '1000';
        hourLabel.style.color = 'white';
        hourLabel.style.textShadow = '0 0 3px black, 0 0 3px black, 0 0 3px black, 0 0 3px black'; // Add black outline
        hourLabel.innerText = 'Hour: 12';

        const dayLabel = document.createElement('div');
        dayLabel.id = 'dayLabel';
        dayLabel.style.position = 'fixed';
        dayLabel.style.bottom = '40px';
        dayLabel.style.left = '30%';
        dayLabel.style.transform = 'translateX(-50%)';
        dayLabel.style.zIndex = '1000';
        dayLabel.style.color = 'white';
        dayLabel.style.textShadow = '0 0 3px black, 0 0 3px black, 0 0 3px black, 0 0 3px black'; // Add black outline
        dayLabel.innerText = 'Day: 1';

        // Append the labels to the body
        document.body.appendChild(hourLabel);
        document.body.appendChild(dayLabel);

        // Add event listeners to handle changes
        hourSlider.addEventListener('input', (event) => {
            this.updateSimulationTime();
        });

        daySlider.addEventListener('input', (event) => {
            this.updateSimulationTime();
        });

        if (!document.getElementById('simButton')) {
            // Create the sun sim on off button
            const simButton = document.createElement('input');
            simButton.type = 'checkbox';
            simButton.id = 'simButton';
            simButton.checked = true;
            // simButton.t = 'Simulation';

            // Style the day slider to position it at the bottom middle of the page
            simButton.style.position = 'fixed';
            simButton.style.bottom = '20px';
            simButton.style.left = `calc(30% + ${parseInt(daySlider.style.width) / 2 + 20}px)`;
            simButton.style.transform = 'translateX(-50%)';
            simButton.style.zIndex = '1000'; // Ensure it is on top of other elements
            // simButton.style.width = '300px'; // Make the slider wider
            const simButtonLabel = document.createElement('div');
            simButtonLabel.id = 'simButtonLabel';
            simButtonLabel.style.position = 'fixed';
            simButtonLabel.style.bottom = '19px';
            simButtonLabel.style.left = `calc(30% + ${parseInt(daySlider.style.width) / 2 + 70}px)`;
            simButtonLabel.style.transform = 'translateX(-50%)';
            simButtonLabel.style.border = '1px solid white';
            simButtonLabel.style.zIndex = '1000';
            simButtonLabel.style.color = 'white';
            simButtonLabel.style.textShadow = '0 0 3px black, 0 0 3px black, 0 0 3px black, 0 0 3px black'; // Add black outline
            simButtonLabel.innerText = 'Simulation';
            document.body.appendChild(simButton);
            document.body.appendChild(simButtonLabel);
            simButton.addEventListener('change', (e: any) => {
                if (!e.target.checked) {
                    this.stopManualSim();
                } else {
                    this.startManulSimulation();
                }
            })

        }


        // Initial update of labels
        this.updateSimulationTime();
    }

    disablePointCloud() {
        const pointCloud: any = document.getElementById('pointCloudVisibilityCheckbox');
        if (pointCloud) {
            pointCloud.checked = false
            const event = new Event('change', {
                bubbles: true,
                cancelable: true,
            });
            pointCloud.dispatchEvent(event);
        }
    }


    stopManualSim() {
        const checkbox: any = document.getElementById("simButton");

        if (checkbox) {
            checkbox.checked = false;
        }

        const light: any = this.engServ.scene.getObjectByName('ambLight')

        if (light) {
            light.intensity = 1;
        }

        document.getElementById('hourSlider')?.remove();
        document.getElementById('daySlider')?.remove();
        document.getElementById('dayLabel')?.remove();
        document.getElementById('hourLabel')?.remove();
        const existingLight = this.engServ.scene.getObjectByName('SimulationLight');
        existingLight?.removeFromParent();
    }

    private updateSimulationTime() {
        const hourSlider = document.getElementById('hourSlider') as HTMLInputElement;
        const daySlider = document.getElementById('daySlider') as HTMLInputElement;
        const hourLabel = document.getElementById('hourLabel') as HTMLDivElement;
        const dayLabel = document.getElementById('dayLabel') as HTMLDivElement;

        const selectedHour = parseInt(hourSlider.value, 10);
        const selectedDay = parseInt(daySlider.value, 10);

        // Calculate the date based on the selected day of the year
        const startOfYear = new Date(new Date().getFullYear(), 0, 0);
        const selectedDate = new Date(startOfYear.getTime() + selectedDay * 24 * 60 * 60 * 1000);
        selectedDate.setHours(selectedHour);

        // Update the labels
        hourLabel.innerText = `Hour: ${selectedHour}`;
        dayLabel.innerText = `Day: ${selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;

        this.simulatetoTime(selectedDate);
    }

    private setRoofsWhite(roofList: string | any[]) {
        for (let i = 0; i < roofList.length; i++) {
            const roofPlane = (this.engServ.scene.getObjectByName(roofList[i].name) as Object3D).getObjectByName(meshTypeEnum.roofPlane) as Mesh;
            const rmaterial = new MeshStandardMaterial({
                side: DoubleSide,
                color: 0xffffff
            });
            roofPlane.material = rmaterial;
            roofPlane.geometry.computeVertexNormals();
        }
    }

    private recoverSceneAfterSimulate(roofList: any[]) {
        this.engServ.scene.remove(this.directionalLight);
        for (const child of this.engServ.scene.children) {
            if (child.name.includes('Tree')) {
                child.children.forEach((c) => {
                    ((c as Mesh).material as MeshStandardMaterial).opacity = 1;
                    ((c as Mesh).material as MeshStandardMaterial).colorWrite = true;
                    ((c as Mesh).material as MeshStandardMaterial).depthWrite = true;
                })
            }
        }
        this.toggleEdgeGroups(true);
        this.toggleObstacleGroups(true);
        for (let i = 0; i < roofList.length; i++) {
            if (roofList[i]) {
                // Guard per-roof: an exception on one roof previously aborted the whole loop
                try {
                    this.engServ.renderer.clear();
                    this.panelGeometry.restorePanelMaterial(roofList[i]);
                    this.panelGeometry.togglePanels(roofList[i], true);
                } catch (error) {
                    console.error('recoverSceneAfterSimulate: failed to restore a roof after shading; continuing with the rest', error);
                }
            }
        }
    }

    private endSimulate() {
        const shadeData: Array<any> = [];
        this.store.select(fromPanelArraySelectors.allPanelArrays).pipe(take(1)).subscribe(panelarrays => {
            panelarrays?.forEach(pArray => {
                const shadeObj = this.panelShadeData.get(pArray.roofId);
                shadeData.push(shadeObj);
            })

            this.store.dispatch(fromShadingActions.SetPanelsData(
                { allPanelsData: shadeData }
            ));
            // Set shading as finished
            this.store.dispatch(fromShadingActions.ChangeHeatMapData(
                { heatMapData: { heatMapStatus: true, btnClicked: false } }));
            this.store.dispatch(fromShadingActions.ChangeShadingStatus(
                { shadingStatus: ShadingStatus.Complete_Success }));
            //this.store.dispatch(fromUndoRedoActions.TriggerURSnapshot({action: fromUndoRedoActions.Shading, notification: this.translateMsg('design.undoRedo.shading',false)}));

            /// Trigger hardware api save when simulation completed to save the panel array shading loss
            this.store.dispatch(fromPanelArrayActions.SaveHardwareDetailsOnly());
            console.log("Shading Time:", new Date().getTime() - this.t);
            this.cleanupResources();
            // Capture shade report specific images (North, South, East, West, Top with shading enabled)
            this.injector.get(ImageCapturer).captureShadeReportImages();

        });
    }
    private eliminateGreyValues(buffer: Uint8ClampedArray) {
        const BLACK = 0x000000;
        const WHITE = 0xFFFFFF;

        for (let i = 0; i < buffer.length; i++) {
            const pixel = buffer[i];
            if (pixel !== BLACK && pixel !== WHITE) {
                buffer[i] = WHITE;
            }
        }
        return buffer;
    }

    private simulation(roofList: any[], show: boolean = false) {
        const monthlyBuffers = [];
        for (let i = 0; i < this.sunPositionArray.length; i++) {
            const hourlyBuffers = [];
            for (let j = 0; j < this.sunPositionArray[i].length; j++) {
                this.adjustDirectionalLight(this.sunPositionArray[i][j], roofList);
                this.engServ.renderer.render(this.engServ.scene, this.orthoCamera);
                let actualBuffer = new Uint8ClampedArray(this.target.width * this.target.height * 4);
                this.engServ.renderer.readRenderTargetPixels(this.target, 0, 0, this.target.width, this.target.height, actualBuffer);
                actualBuffer = this.eliminateGreyValues(actualBuffer);
                if (show) this.imagedata_to_image(new ImageData(actualBuffer, this.target.width, this.target.height))
                hourlyBuffers.push(actualBuffer)
                this.engServ.renderer.clear();
            }
            monthlyBuffers.push(hourlyBuffers);
        }
        this.isRadianceVisible = true;
        return monthlyBuffers;
    }

    private getRoofPanelProperties(roof: Object3D) {
        let panelCoords: Map<string, Vector3[]> | undefined;
        const roofId = roof.name;

        this.store.select(fromPanelArraySelectors.selectPanelArraysByRoofId(roofId))
            .pipe(take(1))
            .subscribe(panelArrays => {
                if (panelArrays?.length) {
                    const panelArrayId = panelArrays[0]?.id;
                    const roofMesh = this.engServ.scene.getObjectByName(roofId) as Mesh;
                    const panelParent = roofMesh?.getObjectByName(panelArrayId) as Object3D;
                    const instancedMesh = panelParent?.getObjectByName(meshTypeEnum.panelArray) as InstancedMesh;
                    if (instancedMesh) {
                        const panelMesh = this.panelGeometry.getAllPanelPoints(instancedMesh, false);
                        panelCoords = this.getPanelProjectedCoordinates(panelMesh, this.target.width, this.target.height, panelArrayId);
                    }
                }
            });

        return { panelCoords };
    }

    private addWorkerListener() {
        this.enactShaderWorker.onerror = () => {
            this.injector.get(ImageCapturer).shadeReportComplete$.next();
        };
        this.enactShaderWorker.addEventListener('message', (e) => {
            if (e.data.message == 'ShadingComplete') {
                const { yearlyBufferWithoutPanels, yearlyBufferWithPanels, roofPanelShadingData }: {
                    yearlyBufferWithoutPanels: Uint8ClampedArray, yearlyBufferWithPanels: Uint8ClampedArray, roofPanelShadingData: Map<string, { panelShadingData: { hourly: any[], monthly: any[], yearly: any[] }, radianceUv: number[] | undefined }>
                }
                    = e.data.data;
                const yearlyTextureBuffer = this.createTextureBuffer(yearlyBufferWithoutPanels, configs.CANVAS_WIDTH / 2, configs.CANVAS_HEIGHT / 2);
                if (Util.debugThreejs) {
                    const yearlyTextureBufferPanel = this.createTextureBuffer(yearlyBufferWithPanels, this.target.width, this.target.height);
                    this.imagedata_to_image(new ImageData(yearlyTextureBufferPanel, this.target.width, this.target.height));
                }
                // this.imagedata_to_image(new ImageData(yearlyTextureBuffer, configs.CANVAS_WIDTH/2, configs.CANVAS_HEIGHT/2));
                // this.setupPanelRadianceMaterial(roofId, value.panelShadingData);

                const texture = new DataTexture(yearlyTextureBuffer, this.target.width, this.target.height, RGBAFormat);
                texture.colorSpace = SRGBColorSpace;
                texture.needsUpdate = true;
                texture.minFilter = LinearFilter;
                texture.magFilter = LinearFilter;
                texture.wrapS = ClampToEdgeWrapping;
                texture.wrapT = ClampToEdgeWrapping;
                const rmaterial = new MeshPhongMaterial({
                    side: DoubleSide,
                    transparent: true,
                    map: texture
                });
                this.radianceMaterialTexture = texture;
                for (const [roofId, value] of roofPanelShadingData) {
                    const roofObject = (this.engServ.scene.getObjectByName(roofId) as Object3D)
                    const roofPlane = roofObject.getObjectByName(meshTypeEnum.roofPlane) as Mesh;
                    roofPlane.material = rmaterial;
                    this.panelShadeData.set(roofId, value.panelShadingData);
                    this.panelGeometry.setPanelShade(roofObject, value.panelShadingData.yearly);

                }
                this.engServ.scene.remove(this.directionalLight);
                // this.setupPanelRadianceMaterial(roofId, value.panelShadingData);
                this.endSimulate();
                this.cleanupResources();
            }
        })
    }

    /**
     * create panel projected coordinates according to texture size,
     * It is independent of target depixelate size
     * @param panels
     * @param columns width of the texture map
     * @param rows length of the texture map
     * @returns
     */
    private getPanelProjectedCoordinates(
        panels: { [key: string]: Vector3[] },
        columns: number,
        rows: number,
        panelArrayId: string
    ): Map<string, Vector3[]> {

        const panelCoords: Map<string, Vector3[]> = new Map();
        const panelIdCache: { [key: number]: string } = {};  // Cache panel IDs by index

        if (panels && Object.keys(panels).length > 0) {
            // Retrieve the panel arrays in bulk synchronously
            this.store
                .select(
                    fromPanelArraySelectors.selectPanelArraysByIds([panelArrayId])
                )
                .pipe(take(1))
                .subscribe((panelArrays) => {
                    if (panelArrays.length <= 0) return;
                    const panelArray = panelArrays[0];


                    // Retrieve the panel data in bulk (panel IDs)
                    this.store
                        .select(
                            fromPanelSelectors.selectPanelsByInstanceIds(
                                Object.keys(panels).map((v) => Number.parseInt(v)), // Get all indices of the panels
                                panelArray?.roofId!
                            )
                        )
                        .pipe(take(1))
                        .subscribe((panelsData) => {
                            panelsData.forEach((panel) => {
                                if (panel) {
                                    panelIdCache[panel.instanceId] = panel.id;
                                }
                            });
                            for (let i = 0; i < Object.keys(panels).length; i++) {
                                const instanceId = Object.keys(panels)[i];

                                const panelVertices = panels[instanceId];
                                const result = panelVertices.map((vertex: Vector3) => {
                                    // Assuming getProjectedCoordinates is synchronous or not causing delay
                                    return this.getProjectedCoordinates(
                                        vertex.clone(),
                                        columns,
                                        rows
                                    );
                                });

                                // Store the result in the panelCoords map
                                (panelCoords as any)[panelIdCache[Number.parseInt(instanceId)]] = result;
                            }
                        });
                });
        }
        return panelCoords;
    }


    private sunPositionArrayFromYear(year: number) {
        const sunPositionArray: Vector3[][] = [];
        const sunriseArray: number[] = [];
        const monthlySunTime: number[] = [];
        // offset from local to UTC
        const localDate = new Date(year, 1, 21, 0, 0, 0);
        const offset = localDate.getTimezoneOffset()/60;
        
        for (let month = 0; month < 12; month++) {
            const date = new Date(year, month, 21, 0, 0, 0);
            const times = SunCalc.getTimes(date, this.latitude, this.longitude);
            const sunrise = times.sunrise;
            const sunset = times.sunset;

            const sunriseTime = sunrise ? sunrise.getHours() + sunrise.getMinutes() / 60 : 0;
            let sunsetTime = sunset ? sunset.getHours() + sunset.getMinutes() / 60 : 0;

            // If sunrise is after sunset, add 24 to sunset to handle overnight sun
            if (sunriseTime > sunsetTime) {
                sunsetTime += 24;
            }

            const sunriseHour = Math.floor(sunriseTime);
            const sunsetHour = Math.ceil(sunsetTime);
            const suntime = sunsetHour - sunriseHour;

            sunriseArray.push(sunriseHour);
            monthlySunTime.push(suntime);
        }

        for (let month = 0; month < 12; month++) {
            const dayArray: Vector3[] = [];
            let sunriseHour = sunriseArray[month];
            const suntime = monthlySunTime[month];

            for (let i = 0; i < suntime; i++) {
                let hour = sunriseHour + i;
                let day = 21;
                const monthIndex = month;

                // If hour exceeds 23, increment the day and wrap hour
                if (hour > 23) {
                    day += Math.floor(hour / 24);
                    hour = hour % 24;
                }

                const timestamp = new Date(year, monthIndex, day, hour, 0, 0);
                const sunPosition = this.getSunLightPosition(timestamp);
                if (sunPosition.z < 10) {
                    sunPosition.z = 10;
                }
                dayArray.push(sunPosition);
            }
            // Ensure times are within 0-24 hour range
            sunriseHour = sunriseHour + (offset + this.utcOffset); // Adjust sunrise hour by UTC offset
            sunriseArray[month] =  Math.floor(((sunriseHour % 24) + 24) % 24);
       
            sunPositionArray.push(dayArray);
        }
        return { sunPositionArray, sunriseArray };
    }

    private createTextureBuffer(buffer: Uint8ClampedArray, rows: number, columns: number) {
        const textureBuffer = new Uint8ClampedArray(rows * columns * 4);
        for (let i = 0; i < rows * columns; i = i + 1) {
            const spec = Util.hexToRgb(colorScale(buffer[i * 4] * 100 / 255));
            if (spec) {
                textureBuffer[4 * i] = spec.r;
                textureBuffer[4 * i + 1] = spec.g;
                textureBuffer[4 * i + 2] = spec.b;
                textureBuffer[4 * i + 3] = 255;
            } else {
                textureBuffer[4 * i + 3] = 255;
            }

        }
        return textureBuffer;
    }

    /**
     * independent of texture size
     * @param v0
     * @param width width of the desired screen
     * @param height height of the desired screen
     * @returns
     */
    private getProjectedCoordinates(v0: Vector3, width: number, height: number) {
        const widthHalf = width / 2, heightHalf = height / 2;

        const pos = v0.clone();
        pos.project(this.orthoCamera);
        pos.x = (pos.x * widthHalf) + widthHalf;
        pos.y = (pos.y * heightHalf) + heightHalf;
        return pos;
    }

    imagedata_to_image(imagedata: ImageData) {
        const canvas = document.createElement('canvas');
        canvas.id = "temporary"
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.zIndex = '1000';
        canvas.style.transform = 'scaleX(-1)'
        const ctx = canvas.getContext('2d');
        if (ctx) {
            canvas.width = imagedata.width;
            canvas.height = imagedata.height;
            ctx.putImageData(imagedata, 0, 0);
            document.body.append(canvas);

            const image = new Image();
            image.src = canvas.toDataURL();
            return image;
        }
        return;

    }

    convertImageDataToBase64(imagedata: ImageData) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = imagedata.width;
        canvas.height = imagedata.height;
        if (ctx) {
            ctx.putImageData(imagedata, 0, 0);
            const base64Image = canvas.toDataURL();
            return base64Image;
        }
        return
    }

    toggleRadiance(radiance: boolean, isFromCaptureImages = false) {
        this.store
            .select(fromRoofSectionSelectors.allRoofSections)
            .pipe(take(1))
            .subscribe((v) => {
                const roofList = v.flatMap((r) => {
                    const objs = this.engServ.scene.getObjectsByProperty('name', r.id);
                    let roofobj: Object3D;
                    objs.forEach(obj => {
                        if (obj.type == 'Object3D') {
                            roofobj = obj;

                        }
                    })
                    return roofobj!;
                })
                for (const roof of roofList) {
                    if (roof) {
                        let fittedRoof
                        for (const m of roof.children) {
                            if (m.name === meshTypeEnum.roofPlane)
                                fittedRoof = m as Mesh;
                        }
                        if (fittedRoof && fittedRoof.parent?.name) {
                            const roofId = fittedRoof.parent?.name
                            // let {panelMeshes, isEastWest} = this.panelGeometry.getPanelsFromRoof(roof, false);
                            const roofMesh = this.engServ.scene.getObjectByName(roofId) as Mesh;
                            const instancedMesh = roofMesh.getObjectByName(meshTypeEnum.panelArray) as InstancedMesh;
                            if (radiance && this.radianceMaterialTexture) {
                                fittedRoof.material = new MeshPhongMaterial({
                                    side: DoubleSide,
                                    transparent: true,
                                    map: this.radianceMaterialTexture
                                });
                                if(instancedMesh){
                                    InstancedMeshUtil.setMultipleTextures(instancedMesh, [], PanelTextureEnum.Shaded, true);
                                }
                            } else {
                                Util.applyUVMap(fittedRoof, this.mapPlane, this.engServ.lowQualityTexture)
                                Util.applyUVMap(fittedRoof, this.mapPlane, this.engServ.lowQualityTexture)
                                if (instancedMesh) {
                                    InstancedMeshUtil.setMultipleTextures(instancedMesh, [], PanelTextureEnum.Normal, true);
                                }
                            }
                        }
                    }
                }
                // When user turn off shading by clicking scene
                if (!radiance && !isFromCaptureImages) {
                    this.radianceMaterialTexture = undefined
                    this.enactShaderWorker?.terminate();
                    if (this.sunPositionArray) {
                        this.sunPositionArray.length = 0;
                    }
                    this.roofPanelProperties?.clear();
                    this.panelShadeData?.clear();
                    this.panelCords?.clear();
                    this.roofPanelProperties?.clear();
                    // To Do: Remove if shading need undo-redo
                    this.store.dispatch(fromUndoRedoActions.TriggerURSnapshot({ action: fromPanelArrayActions.UpsertMany, notification: 'state restored' }))
                }
            })
        this.isRadianceVisible = radiance;

    }
    onPanelHover(instanceId: number | undefined, panelArrayId: string | null) {
        if (!this.isRadianceVisible) return;
        if (this.hoverPanel.instanceId === undefined && instanceId === null)
            return;
        if (this.hoverPanel.instanceId != instanceId) {
            const instancedMeshParent = this.engServ.scene.getObjectByName(
                this.hoverPanel.panelArrayId!
            );
            const instancedMesh = instancedMeshParent?.children[0] as InstancedMesh;
            if (instancedMesh)
                InstancedMeshUtil.setTextureAt(
                    instancedMesh,
                    this.hoverPanel.instanceId!,
                    this.hoverPanel.texture!
                );
            if (instanceId != undefined && panelArrayId) {
                this.store
                    .select(
                        fromPanelArraySelectors.selectPanelArraysByIds([panelArrayId])
                    )
                    .pipe(take(1))
                    .subscribe((panelArrays) => {
                        if (panelArrays.length <= 0) return;
                        this.store
                            .select(
                                fromPanelSelectors.selectPanelsByInstanceIds(
                                    [instanceId],
                                    panelArrays[0]?.roofId!
                                )
                            )
                            .pipe(take(1))
                            .subscribe((panels) => {
                                if (panels.length <= 0) return;
                                const panelId = panels[0].id;
                                this.store.dispatch(
                                    fromShadingActions.SetHoveredPanelId({
                                        hoverPanelId: panelId,
                                    })
                                );
                                this.hoverPanel.texture = this.panelGeometry.hoverPanel(
                                    instanceId,
                                    panelArrayId
                                );
                            });
                    });
                this.hoverPanel.instanceId = instanceId;
                this.hoverPanel.panelArrayId = panelArrayId;
            } else {
                this.store.dispatch(
                    fromShadingActions.SetHoveredPanelId({ hoverPanelId: '' })
                );
                this.hoverPanel = { id: undefined, texture: undefined, instanceId: undefined, panelArrayId: undefined }
            }
        }
    }

    private toggleEdgeGroups(show: boolean) {
        const edgeGroups = this.engServ.scene.getObjectsByProperty('name', meshTypeEnum.edgeGroup);
        edgeGroups.forEach(eg => {
            eg.visible = show;
        });
        const edges = this.engServ.scene.getObjectsByProperty('name', meshTypeEnum.edge);
        edges.forEach(eg => {
            eg.visible = show;
        })

    }

    private toggleAllPanels(roofList: any[], show: boolean) {
        for (let i = 0; i < roofList.length; i++) {
            if (roofList[i]) {
                this.panelGeometry.togglePanels(roofList[i], show);
            }
        }
    }
    private setPanelsWhite(roofList: any[]) {
        for (let i = 0; i < roofList.length; i++) {
            if (roofList[i]) {
                this.panelGeometry.togglePanelsWhite(roofList[i], this.directionalLight);
            }
        }
    }
    private togglePanelsShadow(roofList: any[], show: boolean) {
        for (let i = 0; i < roofList.length; i++) {
            if (roofList[i]) {
                this.panelGeometry.togglePanels(roofList[i], show);
            }
        }
    }

    private toggleObstacleGroups(show: boolean) {
        const obstGroups = this.engServ.scene.getObjectsByProperty('name', meshTypeEnum.obstacleRootGroup) as Group[];
        obstGroups.forEach(oG => {
            const extrCap = oG.getObjectByName(meshTypeEnum.ObstacleExtrudeCap) as Mesh;
            if (extrCap && show) {
                ((extrCap).material as MeshStandardMaterial).opacity = 1;
                ((extrCap).material as MeshStandardMaterial).colorWrite = true;
                ((extrCap).material as MeshStandardMaterial).depthWrite = true;
            } else if (extrCap && !show) {
                ((extrCap).material as MeshStandardMaterial).opacity = 0.01;
                ((extrCap).material as MeshStandardMaterial).colorWrite = false;
                ((extrCap).material as MeshStandardMaterial).depthWrite = false;
            }
            const wallMeshes = oG.getObjectsByProperty('name', 'wallMesh') as Mesh[];
            wallMeshes.forEach((wM: Mesh) => {
                if (show) {
                    ((wM).material as MeshStandardMaterial).opacity = 1;
                    ((wM).material as MeshStandardMaterial).colorWrite = true;
                    ((wM).material as MeshStandardMaterial).depthWrite = true;
                    const lines = wM.children;
                    lines.forEach(line => { line.visible = true })
                } else {
                    ((wM).material as MeshStandardMaterial).opacity = 0.01;
                    ((wM).material as MeshStandardMaterial).colorWrite = false;
                    ((wM).material as MeshStandardMaterial).depthWrite = false;
                    const lines = wM.children;
                    lines.forEach(line => { line.visible = false })

                }
            });
            const children = oG.children;
            children.forEach(c => {
                if (c.type == 'Mesh' && !c.name.includes('ObstacleSetbackMesh')) {
                    if (show) {
                        ((c as Mesh).material as MeshStandardMaterial).opacity = 1;
                        ((c as Mesh).material as MeshStandardMaterial).colorWrite = true;
                        ((c as Mesh).material as MeshStandardMaterial).depthWrite = true;
                    } else {
                        ((c as Mesh).material as MeshStandardMaterial).opacity = 0.01;
                        ((c as Mesh).material as MeshStandardMaterial).colorWrite = false;
                        ((c as Mesh).material as MeshStandardMaterial).depthWrite = false;
                    }
                }
            })

        })
    }


    clearShader() {
        this.panelShadeData = new Map();
        this.radianceMaterialTexture = undefined;
        this.isRadianceVisible = false;
        this.hoverPanel = { id: undefined, texture: undefined, instanceId: undefined, panelArrayId: undefined }
        this.roofPanelProperties = new Map();
        //this.mapPlane = undefined;

    }

    translateMsg(val: string, isToasterMsg = true) {
        let translatedName = '';
        let toasterPrefix = '';
        if (isToasterMsg) {
            toasterPrefix = 'toastrMsgs.';
        }

        this.translate.get(toasterPrefix + val).pipe(take(1)).subscribe(res => {
            translatedName = res;
        }).unsubscribe();
        return translatedName;
    }

}
